import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, today } from "@/lib/api";
import { PageHeader, Card, Btn, Field, Input, Select, Empty } from "@/components/UI";
import { toast } from "sonner";

export default function AccessoriesPage() {
  const [items, setItems] = useState([]);
  const [movs, setMovs] = useState([]);
  const [f, setF] = useState({ name: "", type: "label", unit: "pcs", reorder_level: 0 });
  const [m, setM] = useState({ date: today(), accessory_id: "", qty: "", direction: "in", reference: "" });

  const load = () => {
    api.get("/accessories").then((r) => setItems(r.data));
    api.get("/accessory-movements").then((r) => setMovs(r.data));
  };
  useEffect(() => { load(); }, []);

  const submitAcc = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accessories", { ...f, reorder_level: parseInt(f.reorder_level || 0) });
      toast.success("Accessory added");
      setF({ name: "", type: "label", unit: "pcs", reorder_level: 0 });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const setReorder = async (id, val) => {
    const rl = parseInt(val || 0);
    if (isNaN(rl) || rl < 0) return;
    await api.put(`/accessories/${id}`, { reorder_level: rl });
    load();
  };

  const submitMov = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accessory-movements", { ...m, qty: parseInt(m.qty) });
      toast.success(`Stock ${m.direction === "in" ? "added" : "issued"}`);
      setM({ date: today(), accessory_id: "", qty: "", direction: "in", reference: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader label="Store" title="Accessories" subtitle="Labels, Heat Transfer Stickers, Boxes" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card title="Add Accessory">
          <form onSubmit={submitAcc} className="space-y-3" data-testid="accessory-form">
            <Field label="Name"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Brand Label · HTX · 60x80 Box" /></Field>
            <Field label="Type">
              <Select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                <option value="label">Label</option>
                <option value="sticker">Heat Transfer Sticker</option>
                <option value="box">Box</option>
              </Select>
            </Field>
            <Field label="Unit"><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></Field>
            <Field label="Re-order Level"><Input type="number" value={f.reorder_level} onChange={(e) => setF({ ...f, reorder_level: e.target.value })} placeholder="0 = no alert" /></Field>
            <Btn data-testid="accessory-submit">Add</Btn>
          </form>
        </Card>

        <Card title="Stock Movement">
          <form onSubmit={submitMov} className="space-y-3" data-testid="movement-form">
            <Field label="Date"><Input type="date" required value={m.date} onChange={(e) => setM({ ...m, date: e.target.value })} /></Field>
            <Field label="Item">
              <Select required value={m.accessory_id} onChange={(e) => setM({ ...m, accessory_id: e.target.value })}>
                <option value="">Select…</option>
                {items.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.stock_qty})</option>)}
              </Select>
            </Field>
            <Field label="Direction">
              <div className="grid grid-cols-2 gap-2">
                {["in", "out"].map((d) => (
                  <button key={d} type="button" onClick={() => setM({ ...m, direction: d })}
                    className={`h-9 border font-mono text-xs uppercase tracking-[0.15em] ${m.direction === d ? "bg-foreground text-background border-foreground" : "bg-white border-border"}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Quantity"><Input type="number" required value={m.qty} onChange={(e) => setM({ ...m, qty: e.target.value })} /></Field>
            <Field label="Reference"><Input value={m.reference} onChange={(e) => setM({ ...m, reference: e.target.value })} placeholder="PO / Bill No." /></Field>
            <Btn data-testid="movement-submit">Post Movement</Btn>
          </form>
        </Card>

        <Card title="Current Stock" className="lg:col-span-2">
          <div className="table-scroll">
            <table className="table-dense w-full">
              <thead><tr><th>Name</th><th>Type</th><th>Unit</th><th className="text-right">Stock</th><th className="text-right">Re-order</th></tr></thead>
              <tbody>
                {items.map((a) => {
                  const low = (a.reorder_level || 0) > 0 && a.stock_qty <= a.reorder_level;
                  return (
                    <tr key={a.id} className={low ? "bg-destructive/5" : ""}>
                      <td>{a.name} {low && <span className="ml-1 text-[10px] font-mono uppercase text-destructive">LOW</span>}</td>
                      <td><span className="label-caps">{a.type}</span></td>
                      <td>{a.unit}</td>
                      <td className={`tabular text-right font-bold ${low ? "text-destructive" : ""}`}>{a.stock_qty}</td>
                      <td className="text-right">
                        <input
                          type="number"
                          defaultValue={a.reorder_level || 0}
                          onBlur={(e) => setReorder(a.id, e.target.value)}
                          className="w-16 h-7 px-2 bg-white border border-border font-mono text-xs text-right focus:border-foreground focus:outline-none"
                          data-testid={`acc-reorder-${a.id}`}
                        />
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && <tr><td colSpan={5}><Empty text="No accessories yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div className="px-8 pb-12">
        <Card title="Movement Log">
          <div className="table-scroll">
            <table className="table-dense w-full">
              <thead><tr><th>Date</th><th>Item</th><th>Direction</th><th className="text-right">Qty</th><th>Reference</th></tr></thead>
              <tbody>
                {movs.slice(0, 30).map((mv) => (
                  <tr key={mv.id}>
                    <td className="font-mono">{mv.date}</td>
                    <td>{mv.accessory_name}</td>
                    <td><span className={`label-caps ${mv.direction === "in" ? "text-green-700" : "text-destructive"}`}>{mv.direction}</span></td>
                    <td className="tabular text-right">{mv.qty}</td>
                    <td className="font-mono text-xs">{mv.reference || "—"}</td>
                  </tr>
                ))}
                {movs.length === 0 && <tr><td colSpan={5}><Empty text="No movements yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
